import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import AppNavBar from './AppNavBar';
import ArchieChat from './ArchieChat';
import { useModal } from '../contexts/ModalContext';

const PlannerResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useAuthState(auth);
  const [isSaving, setIsSaving] = useState(false);
  const { showAlert } = useModal();
  const [isSaved, setIsSaved] = useState(false);

  const { lessonPlan, formData, fromLibrary } = location.state || {};

  if (!lessonPlan) {
    navigate('/planner');
    return null;
  }

  const parseLessonPlan = (text) => {
    const sections = {
      title: `${formData.topic} - ${formData.yearGroup} ${formData.subject} Lesson Plan`,
      duration: formData.duration,
      yearGroup: formData.yearGroup,
      subject: formData.subject,
      objectives: [],
      starter: '',
      mainActivities: [],
      plenary: '',
      assessment: '',
      resources: [],
      homework: '',
      teacherNotes: ''
    };

    const activityPattern = /^Activity \d+[:\s].*$/gm;
    const activityMatches = text.match(activityPattern);
    
    if (activityMatches) {
      console.log('Found activities:', activityMatches);
      
      activityMatches.forEach((activityLine, index) => {
        const title = activityLine.trim();
        const activityIndex = text.indexOf(activityLine);
        const nextActivityIndex = index < activityMatches.length - 1 
          ? text.indexOf(activityMatches[index + 1], activityIndex + 1)
          : text.indexOf('Plenary', activityIndex);
        
        const endIndex = nextActivityIndex !== -1 ? nextActivityIndex : text.length;
        const activityText = text.substring(activityIndex + activityLine.length, endIndex);
        
        const content = activityText
          .split('\n')
          .filter(line => line.trim())
          .filter(line => !line.toLowerCase().includes('plenary'))
          .filter(line => !line.toLowerCase().includes('assessment'))
          .filter(line => line.trim().length > 3)
          .slice(0, 10);
        
        if (content.length > 0) {
          sections.mainActivities.push({
            title: title,
            content: content
          });
        }
      });
    }

    if (sections.mainActivities.length === 0) {
      console.log('No activities found with new method, trying fallback');
      
      const chunks = text.split(/(?=Learning Objectives|Lesson Structure|Starter Activity|Main Activities|Plenary|Assessment|Resources|Homework|Teacher Notes)/i);
      
      chunks.forEach(chunk => {
        const lines = chunk.split('\n').filter(line => line.trim());
        if (lines.length === 0) return;
        
        const firstLine = lines[0].toLowerCase();
        
        if (firstLine.includes('main') || firstLine.includes('activities')) {
          const activityContent = lines.slice(1).join('\n');
          
          const activities = activityContent.split(/(?=Activity|^\d+\.|^[A-Z])/m)
            .filter(act => act.trim())
            .slice(0, 6);
          
          activities.forEach((activity, index) => {
            const actLines = activity.split('\n').filter(line => line.trim());
            if (actLines.length > 1) {
              let title = actLines[0].trim();
              if (!title.toLowerCase().includes('activity')) {
                title = `Activity ${index + 1}: ${title}`;
              }
              
              const content = actLines.slice(1).filter(line => 
                line.trim() && line.length > 3
              );
              
              if (content.length > 0) {
                sections.mainActivities.push({
                  title: title,
                  content: content
                });
              }
            }
          });
        }
      });
    }

    const chunks = text.split(/(?=Learning Objectives|Lesson Structure|Starter Activity|Main Activities|Plenary|Assessment|Resources|Homework|Teacher Notes)/i);
    
    chunks.forEach(chunk => {
      const lines = chunk.split('\n').filter(line => line.trim());
      if (lines.length === 0) return;
      
      const firstLine = lines[0].toLowerCase();
      
      if (firstLine.includes('learning objectives') || firstLine.includes('objectives')) {
        const objLines = lines.slice(1).filter(line => {
          const t = line.trim();
          if (!t || t.length < 5) return false;
          // exclude the "By the end..." label line — it's rendered separately in JSX
          if (t.toLowerCase().includes('by the end of this lesson')) return false;
          return true;
        });
        sections.objectives = objLines.slice(0, 5);
      }
      
      else if (firstLine.includes('starter')) {
        const content = lines.slice(1).join(' ').trim();
        if (content) sections.starter = content;
      }
      
      else if (firstLine.includes('plenary')) {
        const content = lines.slice(1)
          .filter(line => 
            line.trim() && 
            !line.toLowerCase().includes('assessment strategies') &&
            !line.toLowerCase().includes('resources required')
          )
          .join(' ').trim();
        if (content) sections.plenary = content;
      }
      
      else if (firstLine.includes('assessment')) {
        const content = lines.slice(1)
          .filter(line => 
            line.trim() && 
            !line.toLowerCase().includes('resources') &&
            !line.toLowerCase().includes('homework')
          )
          .join(' ').trim();
        if (content) sections.assessment = content;
      }
      
      else if (firstLine.includes('resources')) {
        const resourceLines = lines.slice(1).filter(line => 
          line.trim() && 
          (line.includes('-') || line.includes('•') || line.includes('materials'))
        );
        sections.resources = resourceLines.slice(0, 8);
      }
      
      else if (firstLine.includes('homework') || firstLine.includes('extension')) {
        const content = lines.slice(1).join(' ').trim();
        if (content) sections.homework = content;
      }
      
      else if (firstLine.includes('teacher') || firstLine.includes('notes')) {
        const content = lines.slice(1).join(' ').trim();
        if (content) sections.teacherNotes = content;
      }
    });

    console.log('Final parsed activities:', sections.mainActivities);
    return sections;
  };

  const renderFormattedLessonPlan = () => {
    const parsed = parseLessonPlan(lessonPlan);
    
    return (
      <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-coral-500 to-coral-600 text-white p-6">
          <h2 className="text-2xl font-bold text-center mb-4">
            {parsed.title || `${formData.topic} - ${formData.yearGroup} ${formData.subject} Lesson Plan`}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <span className="font-semibold">Duration:</span><br />
              {parsed.duration || formData.duration}
            </div>
            <div className="text-center">
              <span className="font-semibold">Year Group:</span><br />
              {parsed.yearGroup || formData.yearGroup}
            </div>
            <div className="text-center">
              <span className="font-semibold">Subject:</span><br />
              {parsed.subject || formData.subject}
            </div>
            <div className="text-center">
              <span className="font-semibold">Activities:</span><br />
              {formData.numberOfActivities || parsed.mainActivities.length || 'AI-determined'}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 space-y-8">
          
          {/* Learning Objectives */}
          {parsed.objectives.length > 0 && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Learning Objectives
              </h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800 mb-2 font-medium">By the end of this lesson, students will be able to:</p>
                <ul className="space-y-1">
                  {parsed.objectives.map((obj, index) => (
                    <li key={index} className="text-ink-700">{obj}</li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Starter Activity */}
          {parsed.starter && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Starter Activity
              </h3>
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <p className="text-ink-700 leading-relaxed">{parsed.starter}</p>
              </div>
            </section>
          )}

          {/* Main Activities */}
          {parsed.mainActivities.length > 0 && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Main Activities
              </h3>
              <div className="space-y-4">
                {parsed.mainActivities.map((activity, index) => (
                  <div key={index} className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                    <h4 className="font-bold text-green-800 mb-2">{activity.title}</h4>
                    <ul className="space-y-1">
                      {activity.content.map((item, idx) => (
                        <li key={idx} className="text-ink-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Plenary */}
          {parsed.plenary && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Plenary (5 minutes)
              </h3>
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <p className="text-ink-700 leading-relaxed">{parsed.plenary}</p>
              </div>
            </section>
          )}

          {/* Assessment */}
          {parsed.assessment && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Assessment Strategies
              </h3>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <p className="text-ink-700 leading-relaxed">{parsed.assessment}</p>
              </div>
            </section>
          )}

          {/* Resources */}
          {parsed.resources.length > 0 && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Resources Required
              </h3>
              <div className="bg-sand-50 p-4 rounded-lg">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {parsed.resources.map((resource, index) => (
                    <li key={index} className="flex items-center text-ink-700">
                      <span className="w-2 h-2 bg-coral-500 rounded-full mr-3"></span>
                      {resource}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Homework */}
          {parsed.homework && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Homework & Extension
              </h3>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <p className="text-ink-700 leading-relaxed">{parsed.homework}</p>
              </div>
            </section>
          )}

          {/* Teacher Notes */}
          {parsed.teacherNotes && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Teacher Notes
              </h3>
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <p className="text-ink-700 leading-relaxed">{parsed.teacherNotes}</p>
              </div>
            </section>
          )}

          {/* Raw Lesson Plan Fallback */}
          {((formData.numberOfActivities && parsed.mainActivities.length < parseInt(formData.numberOfActivities)) || 
            parsed.mainActivities.some(act => act.content[0]?.includes('Activity content will be generated'))) && (
            <section>
              <h3 className="text-xl font-bold text-coral-600 mb-3 border-b-2 border-coral-200 pb-2">
                Complete Lesson Plan
              </h3>
              <div className="bg-sand-50 p-4 rounded-lg border border-sand-200">
                <p className="text-sm text-ink-600 mb-3">
                  <strong>Note:</strong> Some content may not have parsed correctly above. Here's the complete AI-generated lesson plan:
                </p>
                <div className="bg-white p-4 rounded border text-sm text-ink-700 whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">
                  {lessonPlan}
                </div>
              </div>
            </section>
          )}

        </div>

        {/* Footer */}
        <div className="bg-sand-100 p-4 text-center text-sm text-ink-600">
          <p>Generated by TeAIch Suite • {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    );
  };

  const handlePlanNext = () => {
    navigate('/planner/form', { 
      state: { 
        previousLesson: {
          subject: formData.subject,
          yearGroup: formData.yearGroup,
          topic: formData.topic,
          duration: formData.duration,
          numberOfActivities: formData.numberOfActivities
        }
      }
    });
  };

  const handleSaveToLibrary = async () => {
    if (!user || !lessonPlan || fromLibrary) return;
    
    setIsSaving(true);
    try {
      const lessonData = {
        userId: user.uid,
        lessonPlan: lessonPlan,
        metadata: {
          subject: formData.subject,
          yearGroup: formData.yearGroup,
          topic: formData.topic,
          duration: formData.duration,
          numberOfActivities: formData.numberOfActivities || null,
          learningObjectives: formData.learningObjectives || '',
          priorKnowledge: formData.priorKnowledge || '',
          resources: formData.resources || '',
          differentiation: formData.differentiation || ''
        },
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp()
      };

      await addDoc(collection(db, 'userLessons'), lessonData);
      setIsSaved(true);
      await showAlert('Lesson plan saved to your library! 📚', 'Saved');
    } catch (error) {
      console.error('Error saving lesson:', error);
      await showAlert('Error saving lesson. Please try again.', 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(lessonPlan);
      await showAlert('Lesson plan copied to clipboard!', 'Copied');
    } catch {
      await showAlert('Could not copy to clipboard. Please select and copy manually.', 'Error');
    }
  };

  const handleStartOver = () => {
    navigate('/planner');
  };

  const handleViewLibrary = () => {
    navigate('/planner/library');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              Your Lesson Plan is Ready!
            </h1>
            <p className="text-lg text-ink-700">
              {formData.subject} • {formData.yearGroup} • {formData.duration} • {formData.numberOfActivities ? `${formData.numberOfActivities} Activities` : 'AI-determined Activities'}
            </p>
            {fromLibrary && (
              <p className="text-sm text-ink-600 mt-2">
                📚 Viewing from your lesson library
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
            {!fromLibrary && user && (
              <button
                onClick={handleSaveToLibrary}
                disabled={isSaving || isSaved}
                className={`px-6 py-3 rounded-lg transition-all duration-300 font-semibold flex items-center ${
                  isSaved 
                    ? 'bg-green-500 text-white cursor-not-allowed'
                    : isSaving
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-coral-300 text-white hover:bg-coral-500'
                }`}
              >
                {isSaved ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Saved to Library
                  </>
                ) : isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                    </svg>
                    Save to Library
                  </>
                )}
              </button>
            )}

            {fromLibrary && (
              <button
                onClick={handleViewLibrary}
                className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
                Back to Library
              </button>
            )}

            <button
              onClick={handleCopyText}
              className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
              Copy Text
            </button>
          </div>

          {/* Success Message */}
          {isSaved && (
            <div className="mb-6 max-w-2xl mx-auto">
              <div className="bg-green-100 border-4 border-ink-900 text-green-700 px-4 py-3 rounded relative">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="font-medium">Lesson saved successfully!</span>
                  <button
                    onClick={handleViewLibrary}
                    className="ml-4 underline hover:no-underline"
                  >
                    View in library →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Formatted Lesson Plan Content */}
          <div className="mb-8">
            {renderFormattedLessonPlan()}
          </div>

          {/* Bottom Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={handlePlanNext}
              className="px-8 py-4 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold text-lg flex items-center"
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Plan Next Lesson
            </button>

            <button
              onClick={handleStartOver}
              className="px-8 py-4 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold text-lg"
            >
              Plan Different Lesson
            </button>
          </div>

          {/* Continue Planning Notice */}
          <div className="mt-8 text-center">
            <div className="bg-white bg-opacity-20 rounded-lg border-4 border-ink-900 p-4 max-w-2xl mx-auto">
              <p className="text-ink-900 text-sm">
                <span className="font-semibold">💡 Continue Planning:</span> Click "Plan Next Lesson" to keep the same subject and year group for your lesson sequence. Your context will be preserved to make planning faster.
              </p>
            </div>
          </div>
        </div>
      </div>
      <ArchieChat />
    </div>
  );
};

export default PlannerResultPage;